import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pencil, Video, Plus, Loader2, ChevronRight, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getVideoEmbedInfo } from '@/lib/utils/video';
import { useState } from 'react';

interface VideoIntroductionCardProps {
  videoIntroUrls: string[];
  newVideoUrl: string;
  isEditing: boolean;
  isSaving: boolean;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onVideoUrlChange: (value: string) => void;
  onVideoSelect?: (url: string) => void;
}

/**
 * Normalizes a URL by prepending https:// if it starts with www.
 */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed.startsWith('www.')) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Validates a video URL (YouTube, Vimeo, or Loom)
 * Accepts URLs starting with https:// or www.
 */
function validateVideoUrl(url: string): string | undefined {
  const trimmed = url.trim();
  
  // Empty URLs are allowed (optional - can clear video)
  if (!trimmed) {
    return undefined;
  }

  // Check if URL starts with valid prefix
  const startsWithHttp = trimmed.startsWith('http://') || trimmed.startsWith('https://');
  const startsWithWww = trimmed.startsWith('www.');
  
  if (!startsWithHttp && !startsWithWww) {
    return 'Video URL must start with https:// or www.';
  }

  // Additional check for www. URLs
  if (startsWithWww && trimmed.length <= 4) {
    return 'Please enter a valid video URL';
  }

  // Normalize URL for validation
  const urlToValidate = normalizeUrl(trimmed);

  // Validate URL format
  try {
    new URL(urlToValidate);
  } catch {
    return 'Please enter a valid URL';
  }

  // Check if it's a valid video platform URL
  const videoInfo = getVideoEmbedInfo(urlToValidate);
  if (!videoInfo.type) {
    return 'Please enter a valid YouTube, Vimeo, or Loom URL';
  }

  return undefined; // Validation passed
}

export function VideoIntroductionCard({
  videoIntroUrls,
  newVideoUrl,
  isEditing,
  isSaving,
  onEditClick,
  onCancel,
  onSave,
  onVideoUrlChange,
  onVideoSelect,
}: VideoIntroductionCardProps) {
  const [error, setError] = useState<string | undefined>(undefined);

  const hasVideo = videoIntroUrls.length > 0;
  const primaryUrl = videoIntroUrls[0];
  const videoInfo = primaryUrl ? getVideoEmbedInfo(primaryUrl) : null;

  const handleVideoUrlChange = (value: string) => {
    onVideoUrlChange(value);
    const validationError = validateVideoUrl(value);
    setError(validationError);
  };

  const handleVideoUrlBlur = () => {
    const validationError = validateVideoUrl(newVideoUrl);
    setError(validationError);
  };

  const handleCancel = () => {
    setError(undefined);
    onCancel();
  };

  const handleSave = () => {
    // Validate before saving
    const validationError = validateVideoUrl(newVideoUrl);
    
    // Allow empty URL (to clear video)
    if (newVideoUrl.trim() === '') {
      setError(undefined);
      onSave();
      return;
    }

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(undefined);
    onSave();
  };

  const hasError = error !== undefined;

  return (
    <div className={cn(
      "rounded-2xl sm:rounded-3xl lg:rounded-[40px] p-4 sm:p-6",
      !hasVideo
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200 shadow-sm"
    )}>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Video className={cn(
            "h-5 w-5 flex-shrink-0",
            !hasVideo ? "text-amber-600" : "text-slate-700"
          )} />
          <h3 className="text-lg font-semibold text-slate-900 whitespace-nowrap">Video Introduction</h3>
          {!hasVideo && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-2 py-1 text-xs flex-shrink-0 whitespace-nowrap">
              Recommended
            </Badge>
          )}
        </div>
        {hasVideo && !isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditClick}
            className="text-[#4669ed] flex-shrink-0"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="videoUrl" className="text-sm">Video URL</Label>
            <Input
              id="videoUrl"
              placeholder="https://youtube.com/watch?v=... or www.youtube.com/watch?v=... or https://vimeo.com/... or https://loom.com/share/..."
              value={newVideoUrl}
              onChange={(e) => handleVideoUrlChange(e.target.value)}
              onBlur={handleVideoUrlBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !hasError && !isSaving) {
                  e.preventDefault();
                  handleSave();
                }
              }}
              className={error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {error ? (
              <p className="text-[10px] text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            ) : (
              <p className="text-xs text-slate-500">Supports YouTube, Vimeo, and Loom URLs</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving || hasError}
              className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      ) : !hasVideo ? (
        <div className="flex flex-col items-center text-center py-8">
          <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-4">
            <Video className="h-6 w-6 text-slate-400" />
          </div>
          <h4 className="text-xl text-slate-900 mb-2">Record a video introduction</h4>
          <p className="text-sm text-slate-500 max-w-[260px] mb-6">
            Stand out by adding a 60-90 second video introducing yourself.
            Profiles with videos get 3x more client views.
          </p>
          <Button
            onClick={onEditClick}
            className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] px-6"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Video
          </Button>
        </div>
      ) : videoInfo?.embedUrl ? (
        <div className="space-y-2">
          <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
            <iframe
              src={videoInfo.embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              title="Video Introduction"
            />
          </div>
          {videoInfo.type && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Badge variant="outline" className="text-xs">
                {videoInfo.type}
              </Badge>
              <span>Click fullscreen or use picture-in-picture</span>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => onVideoSelect?.(primaryUrl)}
          className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Video className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <span className="text-sm text-slate-700 truncate">{primaryUrl}</span>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
        </button>
      )}
    </div>
  );
}
